// The CRM import's four `/org/{org_ref}/crm/imports/*` operations (CPB-BE7), beside the
// contact family's thirteen in `crm-transport.ts`. Like those, they use the configured
// `givmoClient` escape hatch: the committed OpenAPI snapshot carries no `/org` family.
//
// Stateless (D-IMP-2): the preview, the dry run and the import each send the same file again,
// as base64 in a JSON body (D-IMP-1). Nothing about the file is kept between steps but here,
// in the browser.

import { givmoClient } from '@givmo/api-client';
import type { CrmImportColumnIn, CrmImportConsentBasis } from './crm-import-wire';

export const CRM_IMPORT_TRANSPORT_OPERATION_COUNT = 4;

export interface CrmImportFile {
  file_name: string;
  content_base64: string;
}

function base(orgRef: string): string {
  return `/org/${encodeURIComponent(orgRef)}/crm/imports`;
}

export async function fetchCrmImportSheet(orgRef: string): Promise<unknown> {
  const { data } = await givmoClient.get({ url: `${base(orgRef)}/sheet`, throwOnError: true });
  return data;
}

export async function previewCrmImport(orgRef: string, file: CrmImportFile): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${base(orgRef)}/preview`,
    body: file,
    throwOnError: true,
  });
  return data;
}

export async function dryRunCrmImport(
  orgRef: string,
  body: CrmImportFile & {
    mapping: CrmImportColumnIn[];
    source_label?: string;
    consent_basis?: CrmImportConsentBasis;
  },
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${base(orgRef)}/dry-run`,
    body,
    throwOnError: true,
  });
  return data;
}

export async function executeCrmImport(
  orgRef: string,
  body: CrmImportFile & {
    mapping: CrmImportColumnIn[];
    source_label: string;
    consent_basis: CrmImportConsentBasis;
    /** A random (version 4) id this import keeps across a double click or a retry. */
    import_id: string;
  },
): Promise<unknown> {
  const { data } = await givmoClient.post({ url: base(orgRef), body, throwOnError: true });
  return data;
}

/** A file's bytes as base64, in chunks, so a large file never builds one huge argument list. */
export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}
